import React from "react";
import type { Transaction, VelaReviewSuggestion } from "../types";
import { formatDate } from "../utils";

interface ReviewCardProps {
  transaction: Transaction;
  suggestion?: VelaReviewSuggestion;
  onPrimary: (transactionId: string) => void;
  onSkip: (transactionId: string) => void;
}

function formatAmount(value: number): string {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

export function ReviewCard({
  transaction: tx,
  suggestion,
  onPrimary,
  onSkip,
}: ReviewCardProps) {
  const primaryLabel = suggestion?.primaryAction ?? "Categorize";
  const skipLabel = suggestion?.skipAction ?? "Skip for now";
  const askText =
    suggestion?.question ??
    "I don't recognize this transaction. Pick a category?";

  const amountClass = tx.direction === "in" ? "text-teal" : "text-ink";
  const amountSign = tx.direction === "in" ? "+" : "−";

  return (
    <div className="py-5 border-b border-lineSoft last:border-b-0 first:pt-0">
      {/* Title row: payee + amount */}
      <div className="flex justify-between items-baseline gap-3 mb-1">
        <div className="text-[14.5px] font-semibold text-ink leading-tight">
          {tx.cleanDescription}
        </div>
        <div className={`text-[14px] font-semibold tabular-nums whitespace-nowrap ${amountClass}`}>
          {amountSign}${formatAmount(tx.amount)}
        </div>
      </div>

      {/* Date + raw description */}
      <div className="text-[11px] text-ink4 font-mono tracking-wide mb-3">
        {formatDate(tx.date)} · {tx.rawDescription}
      </div>

      {/* Vela note: left-bordered quote, no avatar */}
      <div className="text-[13px] text-ink2 leading-relaxed bg-[#FAFBFB] border-l-2 border-vela rounded-r-md px-3 py-2.5 mb-3">
        {askText}
      </div>

      {/* Actions row */}
      <div className="flex justify-end items-center gap-3">
        <button
          type="button"
          onClick={() => onSkip(tx.id)}
          className="text-[12.5px] text-ink3 underline cursor-pointer bg-transparent border-none p-0 font-medium hover:text-ink"
        >
          {skipLabel}
        </button>
        <button
          type="button"
          onClick={() => onPrimary(tx.id)}
          className="h-9 px-4 rounded-lg text-[12.5px] font-semibold border-none bg-ink text-white cursor-pointer hover:bg-ink2"
        >
          {primaryLabel}
        </button>
      </div>
    </div>
  );
}